/**
 * In-page confirmation dialog — replaces the browser-native `confirm()`
 * popup that appears at the top of the viewport.  One ConfirmProvider
 * lives at the App root; useConfirm() returns an async function callers
 * await for a true/false answer.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm("Delete this lead?"))) return;
 *   // proceed…
 *
 * Or with options:
 *
 *   const ok = await confirm({
 *     title:        "Remove member?",
 *     message:      "They lose access immediately.",
 *     confirmLabel: "Remove",
 *     tone:         "danger",
 *   });
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";

const ConfirmCtx = createContext(null);

const DEFAULTS = {
  title: "Are you sure?",
  message: null,
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  tone: "default", // "default" | "danger"
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);  // { opts, resolve }
  const resolverRef = useRef(null);

  // Resolves the pending Promise and tears down.  Idempotent.
  const close = useCallback((answer) => {
    if (resolverRef.current) {
      resolverRef.current(answer);
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  const confirm = useCallback((arg) => {
    const opts = typeof arg === "string"
      ? { ...DEFAULTS, message: arg }
      : { ...DEFAULTS, ...(arg || {}) };
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ opts });
    });
  }, []);

  // Escape closes (= cancel).
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter")  close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && createPortal(
        <ConfirmDialog opts={state.opts} onAnswer={close} />,
        document.body
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(ConfirmCtx);
  if (!fn) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return fn;
}

// ── Dialog ───────────────────────────────────────────────────────────────────

function ConfirmDialog({ opts, onAnswer }) {
  const { title, message, confirmLabel, cancelLabel, tone } = opts;
  const isDanger = tone === "danger";
  const confirmRef = useRef(null);

  // Focus the confirm button on mount so Enter / Space activate it.
  // Tab still cycles to Cancel via natural DOM order.
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-[1px] grid place-items-center p-4 sw-fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onAnswer(false); }}
      data-testid="confirm-dialog"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="w-full max-w-sm bg-white rounded-xl shadow-2xl border border-slate-200 sw-pop"
      >
        <div className="px-5 pt-5 pb-2 flex items-start gap-3">
          {isDanger && (
            <div className="w-9 h-9 rounded-full bg-red-50 grid place-items-center shrink-0">
              <AlertTriangle size={16} className="text-red-600" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 id="confirm-title" className="text-[15.5px] font-bold text-slate-900 leading-snug">
              {title}
            </h2>
            {message && (
              <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed">{message}</p>
            )}
          </div>
          <button
            onClick={() => onAnswer(false)}
            aria-label="Close"
            className="h-7 w-7 grid place-items-center -mt-1 -mr-1 text-slate-400 hover:text-slate-900 rounded-lg hover:bg-slate-100 shrink-0"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-5 pt-3 pb-5 flex justify-end gap-2">
          <button
            onClick={() => onAnswer(false)}
            className="h-9 px-3.5 border border-slate-200 hover:border-slate-400 rounded-lg text-[13px] font-medium text-slate-700 transition"
            data-testid="confirm-cancel"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={() => onAnswer(true)}
            className={
              "h-9 px-4 rounded-lg text-[13px] font-semibold text-white transition active:scale-[0.98] " +
              (isDanger
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700")
            }
            data-testid="confirm-ok"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
